import { ArrowRight, Award, CalendarClock, CalendarPlus, CheckCircle2, CircleDollarSign, Download, FileCheck2, LoaderCircle, Map, MapPin, Megaphone, Navigation, Play, Printer, ReceiptText, RefreshCw, Send, TrendingUp, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiBlob, newIdempotencyKey } from "../api";
import { useAuth } from "../auth";
import { InfoCard, PageBackLink, PageHeader, StateView, StatusBadge } from "../components";
import { fiscalCapabilities } from "../fiscal-permissions";
import { brl, dateTime, invalidateApiData, useApiData } from "../hooks";
import { workOrderNetAmount } from "../money";
import { mutateOffline } from "../offline-queue";
import type { FiscalDocument, FiscalPreflight, WorkOrder, WorkOrderDocument } from "../types";
import { simplifiedOperationalStatus } from "../work-order-status";
import { FieldTools, workOrderCostSummary } from "./FieldTools";

const terminalStatuses = new Set(["completed", "closed", "cancelled", "not_performed"]);
const nextAction = (status: string) => {
  if (terminalStatuses.has(status)) return null;
  return simplifiedOperationalStatus(status) === "in_progress"
    ? { label: "Finalizar serviço", status: "completed" }
    : { label: "Iniciar serviço", status: "in_progress" };
};

const historyLabel = (status: string) => status === "completed" ? "Serviço finalizado" : "Serviço iniciado";

/** Decide se o usuário enviará fieldEvent ao avançar a OS: somente quem está atribuído à
 * OS (vínculo em work_order_employees ou atribuição direta de employee em visita ativa)
 * registra evento de campo. Gestores com schedule.override não são atribuídos e seguem a
 * transição administrativa sem fabricar evento de campo para si. */
export function isActiveAssignee(workOrder: WorkOrder, employeeId: string | null | undefined): boolean {
  return Boolean(employeeId && (
    workOrder.employees?.some(({ employeeId: linked }) => linked === employeeId)
    || workOrder.appointments?.some(({ status, assignments }) =>
      !["rescheduled", "cancelled", "not_performed"].includes(status)
      && assignments.some(({ type, id }) => type === "employee" && id === employeeId)
    )
  ));
}

export function shouldSendFieldEvent(workOrder: WorkOrder, employeeId: string | null | undefined, permissions: readonly string[]): boolean {
  if (!employeeId) return false;
  return !permissions.includes("schedule.override") || isActiveAssignee(workOrder, employeeId);
}
const isReservoirCleaning = (code: string | null, name: string) => {
  if (["WATER_TANK_CLEANING", "CISTERN_CLEANING"].includes(code ?? "")) return true;
  const normalized = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return /(caixa.?d.?agua|cisterna|reservatorio|limpeza.*agua)/.test(normalized);
};

const routeUrls = (address: string) => {
  const destination = encodeURIComponent(address);
  return {
    googleMaps: `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
    waze: `https://www.waze.com/ul?q=${destination}&navigate=yes`,
  };
};

function fiscalCompetence(workOrder: WorkOrder) {
  const completedAt = workOrder.history
    ?.filter(({ axis, toStatus }) => axis === "operational" && toStatus === "completed")
    .at(-1)?.occurredAt;
  const date = new Date(completedAt ?? Date.now());
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", timeZone: "America/Sao_Paulo",
  }).formatToParts(date);
  return `${parts.find(({ type }) => type === "year")?.value}-${parts.find(({ type }) => type === "month")?.value}`;
}

export function WorkOrderDetailPage() {
  const money = brl;
  const { workOrderId } = useParams();
  const { user } = useAuth();
  const { data, error, loading, reload } = useApiData<WorkOrder>(workOrderId ? `/work-orders/${workOrderId}` : null);
  const documents = useApiData<WorkOrderDocument[]>(workOrderId ? `/work-orders/${workOrderId}/documents` : null);
  const {
    canReview: canReadFiscal,
    canIssue: canIssueFiscal,
    canDownload: canDownloadFiscal,
  } = fiscalCapabilities(user?.permissions ?? []);
  const fiscalDocuments = useApiData<FiscalDocument[]>(workOrderId && canReadFiscal ? `/fiscal/documents?workOrderId=${workOrderId}&limit=100` : null);
  const [busy, setBusy] = useState(false);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [noticeBusy, setNoticeBusy] = useState(false);
  const [warrantyBusy, setWarrantyBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [fiscalBusy, setFiscalBusy] = useState<string | null>(null);
  const [fiscalNotice, setFiscalNotice] = useState("");
  const [fiscalReview, setFiscalReview] = useState<FiscalPreflight | null>(null);
  const action = data ? nextAction(data.operationalStatus) : null;
  const displayStatus = data ? simplifiedOperationalStatus(data.operationalStatus) : "planned";
  const visibleHistory = data?.history?.filter(({ axis, toStatus }) => axis === "operational" && ["in_progress", "completed"].includes(toStatus)) ?? [];
  const costSummary = data ? workOrderCostSummary(data) : null;
  const scheduledDate = data ? new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Sao_Paulo" }).format(new Date(data.scheduledStart)) : "";
  const scheduledTime = data ? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" }).format(new Date(data.scheduledStart)) : "";
  const activeAppointments = data?.appointments?.filter(({ status }) => !["cancelled", "not_performed"].includes(status)) ?? [];
  const mayAddAppointment = Boolean(data && ["planned", "preparation", "in_progress", "paused"].includes(data.operationalStatus));
  const fiscalDocumentList = fiscalDocuments.data ?? [];
  const fiscalOperationallyEligible = Boolean(data && ["completed", "closed"].includes(data.operationalStatus));
  const fiscalEligible = Boolean(fiscalOperationallyEligible && data?.financialStatus === "paid");
  const serviceAddress = data?.location?.address?.trim() ?? "";
  const routes = serviceAddress ? routeUrls(serviceAddress) : null;
  const warrantyEligible = data?.items?.some((item) => isReservoirCleaning(item.serviceCode, item.name)) ?? false;
  const warrantyAvailable = Boolean(data && ["completed", "closed"].includes(data.operationalStatus));

  useEffect(() => {
    const pending = fiscalDocumentList.some(({ status }) => ["queued", "processing", "unknown", "cancel_pending"].includes(status));
    if (!pending) return;
    const timer = window.setInterval(() => void fiscalDocuments.reload({ background: true }), 10_000);
    return () => window.clearInterval(timer);
  }, [fiscalDocumentList.map(({ id, status }) => `${id}:${status}`).join("|"), fiscalDocuments.reload]);

  const prepareFiscalDocument = async () => {
    if (!workOrderId || !data) return;
    setFiscalBusy("prepare"); setFiscalNotice(""); setFiscalReview(null);
    try {
      const preflight = await api<FiscalPreflight>("/fiscal/preflight", {
        method: "POST", body: JSON.stringify({ workOrderId }),
      });
      if (!preflight.ready) {
        setFiscalNotice(preflight.issues[0]?.message ?? "Revise os dados fiscais antes de emitir.");
        return;
      }
      setFiscalReview(preflight);
    } catch (caught) { setFiscalNotice(caught instanceof Error ? caught.message : "Não foi possível validar a emissão."); }
    finally { setFiscalBusy(null); }
  };

  const issueFiscalDocument = async (classificationKey: string) => {
    if (!workOrderId || !data || !fiscalReview) return;
    setFiscalBusy(`issue:${classificationKey}`); setFiscalNotice("");
    try {
      await api<FiscalDocument>("/fiscal/documents", {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({
          workOrderId,
          classificationKey,
          competence: fiscalCompetence(data),
          federalWithholdings: { mode: "none" },
        }),
      });
      await fiscalDocuments.reload();
      setFiscalNotice("NFS-e enviada para emissão.");
    } catch (caught) { setFiscalNotice(caught instanceof Error ? caught.message : "Não foi possível iniciar a emissão."); }
    finally { setFiscalBusy(null); }
  };

  const refreshFiscalDocument = async (document: FiscalDocument) => {
    setFiscalBusy(`refresh:${document.id}`); setFiscalNotice("");
    try {
      await api<FiscalDocument>(`/fiscal/documents/${document.id}/reconcile`, { method: "POST" });
      await fiscalDocuments.reload();
    } catch (caught) { setFiscalNotice(caught instanceof Error ? caught.message : "Não foi possível atualizar a NFS-e."); }
    finally { setFiscalBusy(null); }
  };

  const downloadFiscalDocument = async (document: FiscalDocument, kind: "pdf" | "xml") => {
    setFiscalBusy(`${kind}:${document.id}`); setFiscalNotice("");
    try {
      const blob = await apiBlob(`/fiscal/documents/${document.id}/file/${kind}`);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `nfse-${document.nfseNumber ?? document.id}.${kind}`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (caught) { setFiscalNotice(caught instanceof Error ? caught.message : "Documento fiscal indisponível."); }
    finally { setFiscalBusy(null); }
  };

  const downloadServiceOrder = async () => {
    if (!workOrderId) return;
    setDocumentBusy(true); setNotice("");
    try {
      const currentDocuments = await api<WorkOrderDocument[]>(`/work-orders/${workOrderId}/documents`);
      let serviceDocument = currentDocuments.find(({ documentType, status }) => documentType === "service_order" && status === "issued");
      if (!serviceDocument) {
        serviceDocument = await api<WorkOrderDocument>(`/work-orders/${workOrderId}/documents`, {
          method: "POST",
          headers: { "Idempotency-Key": newIdempotencyKey() },
          body: JSON.stringify({ documentType: "service_order" }),
        });
        await documents.reload();
      }
      const blob = await apiBlob(`/documents/${serviceDocument.id}/file`);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `ordem-de-servico-${data?.number ?? serviceDocument.number}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (caught) { setNotice(caught instanceof Error ? caught.message : "Não foi possível baixar a ordem de serviço"); }
    finally { setDocumentBusy(false); }
  };

  const downloadServiceNotice = async () => {
    if (!workOrderId) return;
    setNoticeBusy(true); setNotice("");
    try {
      const blob = await apiBlob(`/work-orders/${workOrderId}/service-notice`);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `comunicado-os-${data?.number ?? workOrderId}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (caught) { setNotice(caught instanceof Error ? caught.message : "Não foi possível gerar o comunicado"); }
    finally { setNoticeBusy(false); }
  };

  const downloadWarrantyCertificate = async () => {
    if (!workOrderId) return;
    setWarrantyBusy(true); setNotice("");
    const warrantyIdempotencyKey = newIdempotencyKey();
    try {
      const currentDocuments = await api<WorkOrderDocument[]>(`/work-orders/${workOrderId}/documents`);
      let warrantyDocument = currentDocuments.find(({ documentType, status }) => documentType === "service_certificate" && status === "issued");
      if (warrantyDocument && data) {
        const snapshotName = (warrantyDocument as WorkOrderDocument & { customerSnapshotName?: string | null }).customerSnapshotName ?? null;
        const isLegacy = snapshotName == null;
        const nameStale = snapshotName != null && snapshotName !== data.customerName;
        if (isLegacy || nameStale) {
          try {
            const refreshed = await api<WorkOrderDocument | null>(`/work-orders/${workOrderId}/warranty`, {
              method: "POST",
              headers: { "Idempotency-Key": warrantyIdempotencyKey },
            });
            if (refreshed?.id) warrantyDocument = refreshed;
            else throw new Error("sem refreshed");
          } catch {
            warrantyDocument = await api<WorkOrderDocument>(`/work-orders/${workOrderId}/documents`, {
              method: "POST",
              headers: { "Idempotency-Key": warrantyIdempotencyKey },
              body: JSON.stringify({ documentType: "service_certificate" }),
            });
          }
          await documents.reload();
        } else {
          try {
            const ensured = await api<WorkOrderDocument | null>(`/work-orders/${workOrderId}/warranty`, {
              method: "POST",
              headers: { "Idempotency-Key": warrantyIdempotencyKey },
            });
            if (ensured && ensured.id !== warrantyDocument.id) {
              warrantyDocument = ensured;
              await documents.reload();
            }
          } catch {
            // mantem vigente se ensure falhar (evita reemissao desnecessaria)
          }
        }
      }
      if (!warrantyDocument) {
        warrantyDocument = await api<WorkOrderDocument>(`/work-orders/${workOrderId}/documents`, {
          method: "POST",
          headers: { "Idempotency-Key": warrantyIdempotencyKey },
          body: JSON.stringify({ documentType: "service_certificate" }),
        });
        await documents.reload();
      }
      const blob = await apiBlob(`/documents/${warrantyDocument.id}/file`);
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `certificado-de-garantia-os-${data?.number ?? workOrderId}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (caught) { setNotice(caught instanceof Error ? caught.message : "Não foi possível emitir a garantia"); }
    finally { setWarrantyBusy(false); }
  };

  const advance = async () => {
    if (!data || !action || !workOrderId) return;
    setBusy(true); setNotice("");
    try {
      const deviceAt = new Date().toISOString();
      const fieldEvent = user?.employeeId && shouldSendFieldEvent(data, user.employeeId, user.permissions)
        ? { employeeId: user.employeeId, deviceAt } : undefined;
      const result = await mutateOffline<Record<string, unknown>>(`/work-orders/${workOrderId}/transitions`, {
        axis: "operational", toStatus: action.status, source: "mobile",
        ...(fieldEvent ? { fieldEvent } : {}),
      }, { "If-Match": `"${data.version}"` });
      if ("queued" in result) setNotice("Ação salva no aparelho. A sincronização ocorrerá quando a conexão voltar.");
      else {
        await reload();
        invalidateApiData("/work-orders");
        invalidateApiData("/appointments");
        invalidateApiData("/dashboard/summary");
        setNotice(action.status === "completed"
          ? "Serviço finalizado. A cobrança já está em A receber."
          : "Serviço iniciado.");
      }
    } catch (caught) { setNotice(caught instanceof Error ? caught.message : "Ação não concluída"); }
    finally { setBusy(false); }
  };

  const renderFiscalDocumentActions = (document: FiscalDocument) => <>
    {["queued", "processing", "unknown", "cancel_pending"].includes(document.status) && <button type="button" className="site-button site-button-outline" disabled={fiscalBusy !== null} onClick={() => void refreshFiscalDocument(document)}><RefreshCw size={16} />{fiscalBusy === `refresh:${document.id}` ? "Atualizando…" : "Atualizar"}</button>}
    {document.documents.pdf && canDownloadFiscal && <button type="button" className="site-button site-button-outline" disabled={fiscalBusy !== null} onClick={() => void downloadFiscalDocument(document, "pdf")}><Download size={16} />{fiscalBusy === `pdf:${document.id}` ? "Baixando…" : "PDF"}</button>}
    {document.documents.xml && canDownloadFiscal && <button type="button" className="site-button site-button-outline" disabled={fiscalBusy !== null} onClick={() => void downloadFiscalDocument(document, "xml")}><Download size={16} />{fiscalBusy === `xml:${document.id}` ? "Baixando…" : "XML"}</button>}
    {document.status === "authorized" && document.documents.pdf && canDownloadFiscal && <Link className="site-button site-button-red work-order-fiscal-share" to={`/ordens/${workOrderId}/nota-fiscal/enviar?documentId=${encodeURIComponent(document.id)}`}><Send size={16} />Enviar</Link>}
    {document.status === "authorized" && !document.documents.pdf && <button type="button" className="site-button site-button-outline" disabled={fiscalBusy !== null} onClick={() => void refreshFiscalDocument(document)}><RefreshCw size={16} />{fiscalBusy === `refresh:${document.id}` ? "Atualizando…" : "Buscar documentos"}</button>}
  </>;

  return <StateView loading={loading} error={error}>
    {data && <>
      <section className="work-order-hero work-order-hero-modern"><div className="app-container">
        <PageBackLink to="/ordens" context="Ordens de serviço" />
        <PageHeader
          title={<span className="work-order-title-line">
            <span>OS {data.number}</span>
            <span className="work-order-title-status-mobile"><StatusBadge value={displayStatus} /></span>
          </span>}
          description={[data.customerName, data.location?.address].filter(Boolean).join(" · ")}
          action={<div className="work-order-header-actions">
            <div className="work-order-document-actions">
              <button type="button" className="site-button site-button-outline work-order-print-action" data-mobile-label={documentBusy ? "" : "OS"} aria-label={documentBusy ? "Emitindo ordem de serviço" : "Emitir OS"} aria-busy={documentBusy} disabled={documentBusy || documents.loading} onClick={() => void downloadServiceOrder()}>{documentBusy ? <LoaderCircle className="work-order-document-spinner spin" size={17} /> : <Printer size={16} />}<span className="work-order-print-label">{documentBusy ? "Emitindo..." : "Emitir OS"}</span></button>
              <button type="button" className="site-button site-button-outline work-order-print-action" data-mobile-label={noticeBusy ? "" : "Comunicado"} aria-label={noticeBusy ? "Emitindo comunicado" : "Emitir comunicado"} aria-busy={noticeBusy} disabled={noticeBusy} onClick={() => void downloadServiceNotice()}>{noticeBusy ? <LoaderCircle className="work-order-document-spinner spin" size={17} /> : <Megaphone size={16} />}<span className="work-order-print-label">{noticeBusy ? "Emitindo..." : "Emitir comunicado"}</span></button>
              {warrantyEligible && <button type="button" className="site-button site-button-outline work-order-print-action" data-mobile-label={warrantyBusy ? "" : "Garantia"} title={warrantyAvailable ? "Emitir certificado de garantia" : "Disponível após finalizar o serviço"} aria-label={warrantyBusy ? "Emitindo certificado de garantia" : "Emitir garantia"} aria-busy={warrantyBusy} disabled={warrantyBusy || documents.loading || !warrantyAvailable} onClick={() => void downloadWarrantyCertificate()}>{warrantyBusy ? <LoaderCircle className="work-order-document-spinner spin" size={17} /> : <Award size={16} />}<span className="work-order-print-label">{warrantyBusy ? "Emitindo..." : "Garantia"}</span></button>}
            </div>
          </div>}
        />
      </div></section>
      <div className="app-container detail-benefits detail-benefits-simple work-order-metrics"><InfoCard icon={CalendarClock} label={activeAppointments.length > 1 ? "Dias de execução" : "Agendamento"} value={activeAppointments.length > 1 ? `${activeAppointments.length} dias` : scheduledTime} detail={activeAppointments.length > 1 ? `${scheduledDate} em diante` : scheduledDate} tone="blue" /><InfoCard icon={CircleDollarSign} label="Valor do serviço" value={money(costSummary?.serviceValue ?? workOrderNetAmount(data))} tone="indigo" /><InfoCard icon={ReceiptText} label="Custo" value={money(costSummary?.total ?? "0.00")} tone="red" /><InfoCard icon={TrendingUp} label="Resultado" value={money(costSummary?.result ?? workOrderNetAmount(data))} tone="green" /></div>
      <section className="app-section detail-section"><div className="app-container">
        {notice && <div className="operation-notice" role="status"><CheckCircle2 size={20} />{notice}</div>}
        <div className="detail-grid work-order-detail-layout">
        <section className="work-order-visits-card">
          <div className="section-title-row">
            <div><span className="site-eyebrow">Agenda da OS</span><h2>Dias de execução</h2></div>
            {mayAddAppointment && <Link className="site-button site-button-outline" to={`/agenda/novo?workOrderId=${data.id}`}><CalendarPlus size={16} />Adicionar dia</Link>}
          </div>
          <div className="work-order-visits-list">
            {activeAppointments.map((appointment, index) => {
              const startsAt = new Date(appointment.startsAt);
              const endsAt = new Date(appointment.endsAt);
              const date = new Intl.DateTimeFormat("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Sao_Paulo" }).format(startsAt);
              const time = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
              return <article key={appointment.id}>
                <span className="work-order-visit-index">{String(index + 1).padStart(2, "0")}</span>
                <div><strong>{date}</strong><small>{time.format(startsAt)}–{time.format(endsAt)} · {appointment.assignments.length} responsável(is)</small></div>
                <StatusBadge value={appointment.status} />
              </article>;
            })}
          </div>
        </section>
        <section className="work-order-history-card">
          <div className="section-title-row">
            <h2>Histórico do serviço</h2>
            {action?.status === "completed" && <button type="button" className="site-button site-button-red work-order-history-action" disabled={busy} onClick={() => void advance()}>{busy ? "Salvando…" : <><CheckCircle2 size={17} />Finalizar serviço</>}</button>}
          </div>
          {visibleHistory.length > 0
            ? <div className="detail-timeline">{visibleHistory.map((event, index) => <article key={event.id}><span className="timeline-number">{String(index + 1).padStart(2, "0")}</span><div><strong>{historyLabel(event.toStatus)}</strong><small>{dateTime(event.occurredAt)}</small></div></article>)}</div>
            : <div className="work-order-history-empty"><strong>Aguardando início</strong>{action?.status === "in_progress" && <button type="button" className="site-button site-button-red work-order-history-action" disabled={busy} onClick={() => void advance()}>{busy ? "Salvando…" : <><Play size={17} />Iniciar serviço</>}</button>}</div>}
        </section>
        <FieldTools workOrder={data} reload={reload} notify={setNotice} />
        <aside className="detail-aside work-order-context-card">
          <article className="work-order-address-card">
            <div className="work-order-address-leading">
              {routes
                ? <a className="work-order-primary-map-link" href={routes.googleMaps} target="_blank" rel="noopener noreferrer" aria-label="Abrir rota no Google Maps" title="Abrir no Google Maps"><span className="aside-icon"><MapPin size={20} /></span></a>
                : <span className="aside-icon work-order-primary-map-icon"><MapPin size={20} /></span>}
              <span className="aside-icon work-order-mobile-map-icon"><MapPin size={20} /></span>
            </div>
            <div className="work-order-address-content">
              <span className="site-eyebrow">Endereço</span><h3>{serviceAddress || "Não informado"}</h3>
              {routes && <nav className="work-order-route-actions work-order-route-actions-mobile" aria-label="Abrir rota para o endereço do serviço">
                <a href={routes.googleMaps} target="_blank" rel="noopener noreferrer" aria-label="Abrir rota no Google Maps"><Map size={15} />Google Maps</a>
                <a href={routes.waze} target="_blank" rel="noopener noreferrer" aria-label="Abrir rota no Waze"><Navigation size={15} />Waze</a>
              </nav>}
            </div>
          </article>
          <article><span className="aside-icon"><Users size={20} /></span><div><span className="site-eyebrow">Equipe</span><h3>{data.employees?.length ?? 0} profissional(is)</h3></div></article>
          <article><span className="aside-icon"><CircleDollarSign size={20} /></span><div><span className="site-eyebrow">Financeiro</span><h3><StatusBadge value={data.financialStatus} /></h3></div></article>
        </aside>
        {canReadFiscal && fiscalOperationallyEligible && <section className="work-order-fiscal-card" aria-labelledby="work-order-fiscal-title">
          <span className="work-order-fiscal-icon"><FileCheck2 size={20} /></span>
          <div className="work-order-fiscal-copy">
            <div><h2 id="work-order-fiscal-title">{fiscalDocumentList.length > 1 ? "Notas fiscais" : "Nota fiscal"}</h2></div>
            <p>{fiscalEligible
              ? fiscalDocumentList.length > 0
                ? `${fiscalDocumentList.length} ${fiscalDocumentList.length === 1 ? "emissão registrada" : "emissões registradas"} para esta OS.`
                : "Os serviços com o mesmo enquadramento são reunidos na mesma NFS-e."
              : "A emissão será liberada após o recebimento integral ser atestado no Financeiro."}</p>
            {fiscalNotice && <small role="status">{fiscalNotice}</small>}
          </div>
          <div className="work-order-fiscal-actions">
            {!fiscalEligible && workOrderId && <Link className="site-button site-button-outline work-order-finance-shortcut" to={`/financeiro?tab=receivables&workOrderId=${encodeURIComponent(workOrderId)}`}><CircleDollarSign size={16} />Ir para A receber<ArrowRight size={15} /></Link>}
            {fiscalEligible && canReadFiscal && !fiscalReview && <button type="button" className="site-button site-button-outline work-order-fiscal-review-trigger" disabled={fiscalBusy !== null} onClick={() => void prepareFiscalDocument()}><FileCheck2 size={16} />{fiscalBusy === "prepare" ? "Validando…" : fiscalDocumentList.length > 0 ? "Ver enquadramentos" : "Revisar emissão"}</button>}
          </div>
          {!fiscalReview && fiscalDocumentList.length > 0 && <div className="work-order-fiscal-documents">
            {fiscalDocumentList.map((document) => <article key={document.id} className="work-order-fiscal-document-row">
              <div>
                <span><strong>{document.nfseNumber ? `NFS-e ${document.nfseNumber}` : "Emissão fiscal"}</strong><StatusBadge value={document.status} /></span>
                <small>{money(document.serviceAmount)}{document.environment === "sandbox" ? " · ambiente de teste" : ""}</small>
                {document.status === "rejected" && document.rejectionMessage && <small className="work-order-fiscal-error">{document.rejectionMessage}</small>}
              </div>
              <div className="work-order-fiscal-actions">{renderFiscalDocumentActions(document)}</div>
            </article>)}
          </div>}
          {fiscalReview && <div className="work-order-fiscal-review">
            <div className="work-order-fiscal-review-heading">
              <div><strong>{fiscalReview.groups.length === 1 ? "1 enquadramento fiscal" : `${fiscalReview.groups.length} enquadramentos fiscais`}</strong><small>{fiscalReview.workOrder.customerName}</small></div>
              <button type="button" className="site-button site-button-outline" disabled={fiscalBusy !== null} onClick={() => setFiscalReview(null)}>Fechar</button>
            </div>
            <div className="work-order-fiscal-groups">
              {fiscalReview.groups.map((group, index) => {
                const document = fiscalDocumentList.find(({ classificationKey }) => classificationKey === group.key)
                  ?? (fiscalReview.groups.length === 1 ? fiscalDocumentList.find(({ classificationKey }) => classificationKey === null) : undefined);
                const mayIssue = !document || ["rejected", "cancelled"].includes(document.status);
                return <article key={group.key} className="work-order-fiscal-group">
                  <div className="work-order-fiscal-group-copy">
                    <span><strong>{fiscalReview.groups.length === 1 ? "Serviços da nota" : `Nota ${index + 1}`}</strong>{document && <StatusBadge value={document.status} />}</span>
                    <h3>{group.items.map(({ serviceName }) => serviceName).join(", ")}</h3>
                    <small>Tributação {[group.classification.nationalTaxCode, group.classification.municipalTaxCode].filter(Boolean).join(" · ")}</small>
                  </div>
                  <strong className="work-order-fiscal-group-amount">{money(group.amount)}</strong>
                  <div className="work-order-fiscal-actions">
                    {mayIssue && canIssueFiscal && <button type="button" className="site-button site-button-red" disabled={fiscalBusy !== null} onClick={() => void issueFiscalDocument(group.key)}><FileCheck2 size={16} />{fiscalBusy === `issue:${group.key}` ? "Enviando…" : document ? "Emitir novamente" : "Emitir NFS-e"}</button>}
                    {document && !mayIssue && renderFiscalDocumentActions(document)}
                  </div>
                  {document?.status === "rejected" && document.rejectionMessage && <small className="work-order-fiscal-error">{document.rejectionMessage}</small>}
                </article>;
              })}
            </div>
          </div>}
        </section>}</div>
      </div></section>
    </>}
  </StateView>;
}
